import { getComparableSmplPntGroupsForOverview } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { useStateWithRef } from '@resistapp/client/hooks/use-state-with-ref';
import {
  getCountryParam,
  OtherParams,
  QueryParams,
} from '@resistapp/client/utils/url-manipulation';
import { antibioticL2Targets, L2Targets, type L2Target } from '@resistapp/common/assays';
import {
  AllProjectSampleTypesGroup,
  ComparableSampleGroupType,
  SmplPntGroup,
} from '@resistapp/common/comparable-sample-groups';
import { SampleType } from '@resistapp/common/sampling-point-types';
import { MetricMode } from '@resistapp/common/types';
import { values } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useAssayContext } from '../../assay-context';
import { useSampleDataContext } from '../../sample-data-context';
import { useSearchParamsContext } from '../../search-params-context';

interface UseQueryParamInitProps {
  metricMode: MetricMode;
}

interface UseQueryParamInitResult {
  queryParamsInitialised: boolean;
  availableSmplPntGroups: SmplPntGroup[] | undefined;
  selectedAntibiotic: L2Target | undefined;
  activeSmplPntGroup: SmplPntGroup | undefined;
  setSelectedCountryStable: (country: string | undefined) => void;
  countryParam: string | null;
}

export function useOverviewQueryParams({ metricMode }: UseQueryParamInitProps): UseQueryParamInitResult {
  const { data, queryFilters } = useSampleDataContext();
  const { assaysLoaded, allAssays } = useAssayContext();
  const { searchParams, searchParamsRef, setSearchParamsStable } = useSearchParamsContext();

  const countryParam = getCountryParam(searchParams);

  const setSelectedCountryStable = useCallback((country: string | undefined) => {
    if (country) {
      searchParamsRef.current.set(OtherParams.COUNTRY, country);
    } else {
      searchParamsRef.current.delete(OtherParams.COUNTRY);
    }
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, []);

  const [availableSmplPntGroups, setAvailableSmplPntGroupsStable, availableSmplPntGroupsRef] =
    useStateWithRef<SmplPntGroup[]>();
  const {
    filters: { selectedTargets, selectedSampleTypeGroup, selectedTargetGrouping },
    setGroupingStable,
    setSampleTypeGroupStable,
  } = queryFilters;

  const selectedAntibiotic =
    queryFilters.filters.selectedTargets.length === 1 &&
    antibioticL2Targets.includes(queryFilters.filters.selectedTargets[0])
      ? queryFilters.filters.selectedTargets[0]
      : undefined;

  const activeSmplPntGroup =
    availableSmplPntGroups?.find(group => group.type === queryFilters.filters.selectedSampleTypeGroup) ||
    availableSmplPntGroups?.[0];

  // 1. UPDATE SELECTABLE ENV GROUPS & QUERY FILTERS ON PROJECT AND METRIC MODE CHANGE
  //    *AND* KEEP QUERY FILTERS IN SUPPORTED STATE FOR OVERVIEW (ON ALL CHANGES)
  const projectIdRef = useRef(data?.id);
  const metricModeRef = useRef<MetricMode | null>(null);
  const queryParamsInitialisedRef = useRef(false);
  useEffect(() => {
    if (!data || !assaysLoaded) {
      return;
    }

    // Detect project and metric mode changes
    const projectChanged = data.id !== projectIdRef.current; // NOTE: focusedByUID data filtering is not noticed by this check
    const metricModeChanged = metricMode !== metricModeRef.current;
    projectIdRef.current = data.id;
    metricModeRef.current = metricMode;

    const shouldRefreshGroups = projectChanged || metricModeChanged || !availableSmplPntGroupsRef.current?.length;
    const freshSelectableSmplPntGroups = shouldRefreshGroups
      ? getComparableSmplPntGroupsForOverview(data.samplesByUID, metricMode, data.processes)
      : (availableSmplPntGroupsRef.current as SmplPntGroup[]);

    if (shouldRefreshGroups) {
      setAvailableSmplPntGroupsStable(freshSelectableSmplPntGroups);
    }
    const selectedGroupIsSupported = freshSelectableSmplPntGroups.some(group => group.type === selectedSampleTypeGroup);
    const userChangedOnlySampleType = !projectChanged && !metricModeChanged && selectedGroupIsSupported;
    if (userChangedOnlySampleType) {
      // Let the user selection stick; skip auto-adjustments
      queryParamsInitialisedRef.current = true;
      return;
    }

    // Update sample type group and gene groups to supported value when needed
    let paramsUpdated = false;
    if (
      metricModeChanged &&
      metricMode !== MetricMode.REDUCTION &&
      selectedSampleTypeGroup === ComparableSampleGroupType.WATER_TREATMENT &&
      freshSelectableSmplPntGroups.find(group => group.type === ComparableSampleGroupType.TREATED_WASTEWATER)
    ) {
      // Auto-select TREATED_WASTEWATER
      setSampleTypeGroupStable(ComparableSampleGroupType.TREATED_WASTEWATER, false);
      paramsUpdated = true;
    } else if (
      metricModeChanged &&
      metricMode === MetricMode.REDUCTION &&
      (selectedSampleTypeGroup === SampleType.WASTEWATER ||
        values(ComparableSampleGroupType).includes(selectedSampleTypeGroup as ComparableSampleGroupType)) &&
      selectedSampleTypeGroup !== ComparableSampleGroupType.WATER_TREATMENT &&
      freshSelectableSmplPntGroups.find(group => group.type === ComparableSampleGroupType.WATER_TREATMENT)
    ) {
      // Auto-select WATER_TREATMENT
      setSampleTypeGroupStable(ComparableSampleGroupType.WATER_TREATMENT, false);
      paramsUpdated = true;
    } else if (
      // Default, empty stype param is treated as automatic switching based on metric mode
      // TODO: changes this behaviour and allow showing data across smplPnt types when explicitly requested
      selectedSampleTypeGroup !== AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS &&
      !selectedGroupIsSupported
    ) {
      setSampleTypeGroupStable(AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS, true);
      paramsUpdated = true;
    }

    // We only support assay with selected assay and antibiotic grouping in overview, others can cause errors
    // If grouping is set to 'assay' but no assay or all assays are selected, remove the grouping
    else if (
      (selectedTargetGrouping !== 'assay' && selectedTargetGrouping !== 'antibiotic') ||
      (selectedTargetGrouping === 'assay' &&
        (!selectedTargets.length ||
          !selectedTargets.some(g => typeof g === 'string' && g.startsWith('AY')) ||
          selectedTargets.length === allAssays.length)) ||
      (selectedTargetGrouping === 'antibiotic' &&
        selectedTargets.length !== 1 &&
        antibioticL2Targets.includes(selectedTargets[0] as L2Target))
    ) {
      setGroupingStable(null);
    }

    const singleTarget = selectedTargets.length === 1 ? (selectedTargets[0] as string) : undefined;
    const isAntibiotic = singleTarget && antibioticL2Targets.includes(singleTarget as L2Target);
    const isL2 =
      singleTarget && typeof singleTarget === 'string' && values(L2Targets).includes(singleTarget as L2Targets);
    if (!singleTarget || (!isAntibiotic && isL2)) {
      searchParamsRef.current.delete(QueryParams.GENE_GROUPS);
      paramsUpdated = true;
    }

    // // Force selectedTargetGrouping to antibiotic and allow only antibiotic in selected targets
    // if (
    //   selectedTargetGrouping !== 'antibiotic' ||
    //   selectedTargets.find(target => getGroup(target, 'l1Target') !== L1Targets.ARG) ||
    //   !selectedTargets.length
    // ) {
    //   setGroupingStable('antibiotic');
    //   toggleGeneGroupStable(antibioticL2Targets, true, 'antibiotic');
    //   paramsUpdated = true;
    // }

    // If we modified the searchParamsRef, make sure the change is pushed to the URL
    if (paramsUpdated) {
      // Persist updated URL search parameters
      setSearchParamsStable(searchParamsRef.current, { replace: true });

      // Wait for the next render cycle before marking the query params as initialised
      setTimeout(() => {
        queryParamsInitialisedRef.current = true;
      }, 0);
    } else if (!queryParamsInitialisedRef.current) {
      queryParamsInitialisedRef.current = true;
    }
  }, [
    data,
    metricMode,
    selectedTargets,
    selectedSampleTypeGroup,
    selectedTargetGrouping,
    assaysLoaded,
    allAssays.length,
  ]);

  return {
    availableSmplPntGroups,
    selectedAntibiotic,
    activeSmplPntGroup,
    setSelectedCountryStable,
    countryParam,
    queryParamsInitialised: queryParamsInitialisedRef.current,
  };
}
